import React from 'react';

import {
  Chart as ChartJS,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useThemeContext } from '@infoods/lifebase-ui';

import classes from './DemographicsGraph.module.scss';
import { ClientDemographicData } from '../../../../interfaces/entity-dashboard/ClientDemographicData';
import { DEFAULT_AGE_RANGES } from '../../../../constants/ageRanges';
import {
  formatAgeRangeLabel,
  getChartBorder,
  getTooltipBaseOptions,
} from '../../../../../shared/features/charts';

ChartJS.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip);

type DemographicsGraphProps = {
  graphData: ClientDemographicData['graphData'];
};

const DemographicsGraph = ({ graphData }: DemographicsGraphProps) => {
  const { palette } = useThemeContext();

  const borderPlugin = getChartBorder(palette);
  const tooltipBaseOptions = getTooltipBaseOptions(palette);
  const labels = DEFAULT_AGE_RANGES.map(formatAgeRangeLabel);
  const femaleData = graphData.map((item) => item.female);
  const maleData = graphData.map((item) => item.male);

  const fontCaption12Medium = {
    family: "'Lato', sans-serif",
    size: 12,
    weight: 500,
  } as const;

  const ticksOptions = {
    color: palette.neutral[600],
    font: fontCaption12Medium,
    maxRotation: 0,
  };

  return (
    <div className={classes.graph}>
      <Bar
        data={{
          labels,
          datasets: [
            {
              data: femaleData,
              label: 'Female Customers',
              backgroundColor: palette.violet[100],
              categoryPercentage: 0.4,
              barPercentage: 0.7,
            },
            {
              data: maleData,
              label: 'Male Customers',
              backgroundColor: palette.info[200],
              categoryPercentage: 0.4,
              barPercentage: 0.7,
            },
          ],
        }}
        options={{
          animation: false,
          maintainAspectRatio: false,
          elements: {
            point: {
              radius: 0,
              hoverRadius: 0,
            },
          },
          interaction: {
            mode: 'index',
            intersect: false,
          },
          scales: {
            x: {
              display: true,
              border: { color: palette.neutral[200], width: 1, dash: [2, 2] },
              ticks: {
                ...ticksOptions,
                autoSkip: false,
                padding: 0,
              },
              grid: {
                tickColor: 'transparent',
                color: palette.neutral[200],
              },
            },
            y: {
              position: 'right',
              beginAtZero: true,
              border: { color: palette.neutral[200], width: 1, dash: [2, 2] },
              grid: {
                tickColor: 'transparent',
                color: palette.neutral[200],
              },
              ticks: {
                ...ticksOptions,
                maxTicksLimit: 5,
              },
            },
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              ...tooltipBaseOptions,
            },
          },
        }}
        plugins={[borderPlugin]}
      />
    </div>
  );
};

export { DemographicsGraph };
